import React, { createContext, useContext, useRef, useState } from "react";
import axiosInstance from "../Config/axios";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const PaymentContext = createContext();
export const usePayment = () => useContext(PaymentContext);

export const PaymentProvider = ({ children }) => {
  const toastRef = useRef(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

 
  const createSetupIntent = async () => {
    const accessToken = localStorage.getItem("accessToken");

    if (!accessToken) {
      toastRef.current?.show({
        severity: "error",
        summary: "Unauthorized",
        detail: "Access token missing. Please log in again.",
        life: 4000,
      });
      localStorage.clear();
      navigate("/login", { replace: true });
      return null;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(
        "/payment/create-setup-intent/",
        {}, 
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      console.log("Setup Intent Response:", response.data);

      setClientSecret(response.data.client_secret);
      setLoading(false);
      return response.data.client_secret; 
    } catch (err) {
      const errorDetail =
        err.response?.data?.detail || "Failed to create setup intent";

      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: errorDetail,
        life: 3000,
      });

      setLoading(false);
      return null;
    }
  };

  // Sync Card API 
 
  const syncCard = async (paymentMethodId) => {
    const accessToken = localStorage.getItem("accessToken");

    if (!accessToken) {
      toastRef.current?.show({
        severity: "error",
        summary: "Unauthorized",
        detail: "Access token missing. Please log in again.",
        life: 4000,
      });
      localStorage.clear();
      navigate("/login", { replace: true });
      return null;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(
        "/payment/payment-methods/sync/",
        { payment_method_id: paymentMethodId },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      Swal.fire({
        icon: "success",
        title: "Card Saved Successfully",
        text: response.data.message || "Your card has been added successfully.",
        confirmButtonColor: "#B79527", // orange / gold
      cancelButtonColor: "#1C1C1C",
      });

      setLoading(false);
      return response.data;
    } catch (err) {
      const errorDetail =
        err.response?.data?.detail || "Failed to sync your card";

      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorDetail,
        confirmButtonColor: "#B79527", // orange / gold
      cancelButtonColor: "#1C1C1C",
      });

      setLoading(false);
      return null;
    }
  };

  // =========================
// Fetch saved payment methods
// =========================
const getPaymentMethods = async () => {
  const accessToken = localStorage.getItem("accessToken");

  if (!accessToken) {
    toastRef.current?.show({
      severity: "error",
      summary: "Unauthorized",
      detail: "Access token missing. Please log in again.",
      life: 4000,
    });
    localStorage.clear();
    navigate("/login", { replace: true });
    return null;
  }

  try {
    setLoading(true);
    const response = await axiosInstance.get("/payment/payment-methods/", {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    console.log("Payment Methods Response:", response.data);
    setLoading(false);

    return response.data; // This should be the list of saved cards
  } catch (err) {
    const errorDetail =
      err.response?.data?.detail || "Failed to fetch payment methods";

    toastRef.current?.show({
      severity: "error",
      summary: "Error",
      detail: errorDetail,
      life: 3000,
    });

    setLoading(false);
    return null;
  }
};

// =========================
// Remove a saved payment method
// =========================
const removeCard = async (cardId) => {
  const accessToken = localStorage.getItem("accessToken");

  if (!accessToken) {
    toastRef.current?.show({
      severity: "error",
      summary: "Unauthorized",
      detail: "Access token missing. Please log in again.",
      life: 4000,
    });
    localStorage.clear();
    navigate("/login", { replace: true });
    return null;
  }

  try {
    setLoading(true);
    const response = await axiosInstance.delete(
      `/payment/payment-methods/${cardId}/delete/`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    Swal.fire({
      icon: "success",
      title: "Card Removed",
      text: response.data.message || "Your card has been removed successfully.",
      confirmButtonColor: "#f7931a",
      cancelButtonColor: "#1C1C1C",
    });

    setLoading(false);
    return true; 
  } catch (err) {
    const errorDetail =
      err.response?.data?.detail || "Failed to remove card";

    Swal.fire({
      icon: "error",
      title: "Error",
      text: errorDetail,
      confirmButtonColor: "#B79527", // orange / gold
      cancelButtonColor: "#1C1C1C",
    });

    setLoading(false);
    return false;
  }
};


  return (
    <PaymentContext.Provider
      value={{
        loading,
        clientSecret,
        createSetupIntent,
        syncCard,
          getPaymentMethods,
           removeCard
      }}
    >
      <Toast ref={toastRef} />
      {children}
    </PaymentContext.Provider>
  );
};
